import React, { useState, useEffect } from 'react';
import { ChevronLeft, Stethoscope, UserRoundSearch } from 'lucide-react';
import GlobalLoader from './GlobalLoader';
import DoctorCard from './DoctorCard';
import DoctorScheduleModal from './DoctorScheduleModal';
import { doctorService } from '../services/doctorService';
import { extractDurationMinutes } from '../services/scheduleModels';

const DoctorSelection = ({ specialty, onSelect, onBack, availabilityData, utmParams, allSpecialties = [], allSpecialtyConfig = {}, campaignDurationMap = {}, onOndemandRequest, loggedInUser }) => {
  // Estados
  const [doctors, setDoctors] = useState([]);
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleModalContext, setScheduleModalContext] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters] = useState({ availability: 'all', rating: 'all' });

  // ─── Build a fully-enriched specialty object ───
  // The specialty prop may arrive without duration data (e.g. when getSpecialtyConfig returns null
  // and the inline fallback {id, name, icon} is used). In that case we look up the data ourselves.
  const fullSpecialty = React.useMemo(() => {
    const specId = specialty?.id;
    if (!specId) return specialty;

    // 1. Check if the prop already has duration data
    if ((specialty?.allDurations?.length > 0) || (specialty?.durations?.length > 0) ||
        (specialty?.allExtraDurations?.length > 0) || (specialty?.extra_durations?.length > 0)) {
      return specialty;
    }

    // 2. Look up from Firebase specialtyConfig (keyed by Odoo ID)
    let config = allSpecialtyConfig[specId];

    // 3. Fallback: look up from specialties array
    if (!config || (!config.durations?.length && !config.extra_durations?.length)) {
      const fromList = allSpecialties.find(s => s.id === specId || s.odooId === specId);
      if (fromList) {
        config = {
          ...specialty,
          duration: fromList.durationId,
          extraDuration: fromList.extraDurationId,
          durations: fromList.durations || [],
          extra_durations: fromList.extra_durations || [],
          name: fromList.name || specialty.name,
          icon: fromList.icon || specialty.icon,
        };
      }
    }

    if (!config) return specialty;

    // 4. Merge with campaign data if available
    const campaignData = campaignDurationMap[specId];
    const baseDurations = config.durations || [];
    const baseExtraDurations = config.extra_durations || [];

    if (campaignData) {
      const cAll = campaignData.allDurations || [];
      const cExtra = campaignData.allExtraDurations || [];
      return {
        ...config,
        ...specialty,
        durations: baseDurations,
        extra_durations: baseExtraDurations,
        allDurations: cAll.length > 0 ? cAll : baseDurations,
        allExtraDurations: cExtra.length > 0 ? cExtra : baseExtraDurations,
        duration: campaignData.includedDurationId || config.duration,
        extraDuration: campaignData.extraDurationId || config.extraDuration,
      };
    }

    return {
      ...config,
      ...specialty,
      allDurations: baseDurations,
      allExtraDurations: baseExtraDurations,
    };
  }, [specialty, allSpecialties, allSpecialtyConfig, campaignDurationMap]);

  // Duraciones para el selector 30/60 min del modal. Igual que la app Flutter:
  // solo las incluidas; el backend devuelve los mismos slots para el id incluido
  // y el extra de los mismos minutos (verificado contra producción), y el pago
  // extra mapea incluida→extra por minutos en checkout (resolveExtraDurationId).
  // Fallback a extra_durations solo para especialidades extra-only
  // (ej. Nefrología: durations=[], extra_durations=[102]).
  const includedDurations = (fullSpecialty?.allDurations || fullSpecialty?.durations || []).filter(d => d && d.id && !d.is_extra_duration);
  const extraDurations = fullSpecialty?.allExtraDurations || fullSpecialty?.extra_durations || [];

  // Función para obtener doctoras
  const loadDoctors = async () => {
    if (!fullSpecialty || !fullSpecialty.id) {
      setError('Por favor, selecciona una especialidad primero');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const specialtyId = fullSpecialty.id;
      const data = await doctorService.getDoctorsBySpecialty(specialtyId);
      if (data && data.length > 0) {
        const sorted = data.sort((a, b) => {
          if (b.rating_professional !== a.rating_professional) {
            return b.rating_professional - a.rating_professional;
          }
          return b.consultation_minutes - a.consultation_minutes;
        });

        setDoctors(sorted);
        setFilteredDoctors(sorted);
      } else {
        setError('No hay doctoras disponibles para esta especialidad.');
        setDoctors([]);
        setFilteredDoctors([]);
      }
    } catch (err) {
      setError('Error al cargar las doctoras. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Cargar doctoras cuando cambie la especialidad
  useEffect(() => {
    loadDoctors();
  }, [fullSpecialty?.id]);

  // Seleccionar doctora
  const handleSelectDoctor = (doctor) => {
    setSelectedDoctor(doctor);
  };

// Ver horarios de una doctora — abre el modal que fetchea sus propios datos
// usando el endpoint unificado (get_professional_schedule_today_to_three_days_after).
const handleViewSchedule = (doctor) => {
  const isExtraOnly = includedDurations.length === 0 && extraDurations.length > 0;
  const sourceDurations = isExtraOnly ? extraDurations : includedDurations;

  const durationsForModal = sourceDurations
    .filter(d => d && d.id)
    .map(d => ({ id: d.id, isExtra: isExtraOnly, durationMinutes: extractDurationMinutes(d) }));

  if (durationsForModal.length === 0) {
    const fallbackId = fullSpecialty?.duration_id || fullSpecialty?.duration || 109;
    durationsForModal.push({ id: fallbackId, isExtra: false, durationMinutes: null });
  }

  setScheduleModalContext({
    doctor,
    durations: durationsForModal,
  });
  setShowScheduleModal(true);
};

  // Filtros y búsqueda
  useEffect(() => {
    let result = [...doctors];

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(doctor => 
        `${doctor.name} ${doctor.last_name}`.toLowerCase().includes(term) ||
        doctor.specialty?.name?.toLowerCase().includes(term) ||
        doctor.tag_ids?.some(tag => tag.name.toLowerCase().includes(term))
      );
    }

    if (filters.availability === 'available') {
      result = result.filter(doctor => doctor.has_availability === true);
    }

    if (filters.rating === '4.5+') {
      result = result.filter(doctor => doctor.rating_professional >= 4.5);
    } else if (filters.rating === '4.0+') {
      result = result.filter(doctor => doctor.rating_professional >= 4.0);
    }

    setFilteredDoctors(result);
  }, [doctors, searchTerm, filters]);

  // Si está cargando
  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 min-h-screen">
        <button 
          onClick={onBack}
          className="press-scale mb-6 text-daisies flex items-center gap-2 hover:gap-3 transition-all font-medium"
        >
          <ChevronLeft size={20} />
          Volver a métodos de agendamiento
        </button>
        <GlobalLoader 
          title={`Buscando doctoras en ${specialty?.name || 'Psicología'}`}
          subtitle="Cargando las mejores doctoras para ti..." 
          overlay={false} 
        />
      </div>
    );
  }

  // Si hay error
  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 min-h-screen">
        <button 
          onClick={onBack}
          className="press-scale mb-6 text-daisies flex items-center gap-2 hover:gap-3 transition-all font-medium"
        >
          <ChevronLeft size={20} />
          Volver
        </button>
        
        <div className="text-center">
          <h2 className="text-2xl font-bold text-text-primary mb-4 tracking-tight">Error al cargar</h2>
          <p className="text-red-600 mb-6">{error}</p>
          
          <div className="flex gap-3 justify-center">
            <button
              onClick={loadDoctors}
              className="press-scale px-6 py-3 bg-daisies text-white rounded-full font-semibold hover:bg-daisies-hover transition-all"
            >
              Reintentar
            </button>
            <button
              onClick={onBack}
              className="press-scale px-6 py-3 border-[1.5px] border-brand text-brand rounded-full font-semibold hover:bg-brand-tint transition-colors"
            >
              Volver
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-3 md:px-4 py-3 md:py-6 min-h-screen">
      {/* Header - más compacto en móvil */}
      <button 
        onClick={onBack}
        className="press-scale mb-3 md:mb-4 text-daisies flex items-center gap-1 md:gap-2 hover:gap-2 md:hover:gap-3 transition-all text-xs md:text-sm font-medium"
      >
        <ChevronLeft size={16} />
        Volver
      </button>

      {/* Información de especialidad - más compacta */}
      <div className="mb-4 md:mb-6">
        <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
          <span className="flex items-center justify-center w-11 h-11 rounded-full bg-brand-tint text-brand shrink-0">
            <Stethoscope size={22} aria-hidden="true" />
          </span>
          <div>
            <h1 className="text-lg md:text-2xl font-bold text-brand-deep leading-tight tracking-tight">
              Elige a tu doctora
            </h1>
            <p className="text-text-secondary text-xs md:text-sm mt-0.5">
              {specialty?.name || 'Especialidad'} • {doctors.length} {doctors.length === 1 ? 'doctora' : 'doctoras'}
            </p>
          </div>
        </div>
      </div>

      {/* Lista de doctoras */} 
      <div className="mb-4 md:mb-6">
        {filteredDoctors.length > 0 ? (
          <div className="space-y-3 md:space-y-4">
            {filteredDoctors.map(doctor => (
              <DoctorCard
                key={doctor.id}
                doctor={doctor}
                onSelect={handleSelectDoctor}
                isSelected={selectedDoctor?.id === doctor.id}
                onViewSchedule={handleViewSchedule}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-6 md:py-8 bg-surface-card rounded-card">
            <UserRoundSearch className="mx-auto mb-3 text-brand/50" size={40} aria-hidden="true" />
            <h3 className="text-base md:text-lg font-semibold text-brand-deep mb-1 md:mb-2">
              No hay doctoras disponibles
            </h3>
            <p className="text-text-secondary text-xs md:text-sm mb-3 md:mb-4">
              {searchTerm 
                ? 'No se encontraron doctoras con los filtros actuales.' 
                : 'Actualmente no hay doctoras disponibles para esta especialidad.'}
            </p>
            <div className="flex gap-2 justify-center">
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="press-scale px-3 py-1.5 border border-daisies/20 text-daisies rounded-full font-semibold hover:bg-daisies-50 transition-all text-xs"
                >
                  Limpiar
                </button>
              )}
              <button
                onClick={loadDoctors}
                className="press-scale px-3 py-1.5 bg-daisies text-white rounded-full font-semibold hover:bg-daisies-hover transition-all text-xs"
              >
                Reintentar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de horarios — fetchea sus propios slots con scheduleService */}
      {showScheduleModal && scheduleModalContext && (
        <DoctorScheduleModal
          doctor={scheduleModalContext.doctor}
          durations={scheduleModalContext.durations}
          authToken={loggedInUser?.authToken || ''}
          userId={loggedInUser?.daisUserId || loggedInUser?.userId}
          onSelect={(slot) => {
            onSelect({
              ...slot,
              doctor: scheduleModalContext.doctor,
              professional_ids: slot.professional_ids?.length > 0
                ? slot.professional_ids
                : [{ id: scheduleModalContext.doctor.id }],
              professional_id: scheduleModalContext.doctor.id,
            });
            setShowScheduleModal(false);
          }}
          onClose={() => {
            setShowScheduleModal(false);
            setScheduleModalContext(null);
          }}
          onOndemandRequest={onOndemandRequest}
        />
      )}
    </div>
  );
};

export default DoctorSelection;